import { useCallback, useMemo } from 'react';
import useSWRInfinite, { type SWRInfiniteKeyLoader } from 'swr/infinite';
import type { PaginatedResponse } from '../utils/pagination';

type FetchPage<T> = (query: { limit: string; offset: string }) => Promise<PaginatedResponse<T>>;

export type UseInfiniteListResult<T> = {
  items: T[];
  itemCount: number;
  loadedCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  loadMore: () => void;
  refresh: () => void;
};

/**
 * Server-paginated infinite list backed by SWR Infinite.
 *
 * The SWR cache key is `${cacheKey}|p=<pageIndex>` per loaded page so adjacent
 * lists do not collide. Pass a stable `cacheKey` (string) per logical list.
 */
export const useInfiniteList = <T>({
  cacheKey,
  pageSize,
  fetchPage,
}: {
  cacheKey: string;
  pageSize: number;
  fetchPage: FetchPage<T>;
}): UseInfiniteListResult<T> => {
  const getKey: SWRInfiniteKeyLoader<PaginatedResponse<T>> = (
    pageIndex: number,
    prev: PaginatedResponse<T> | null
  ): string | null => {
    if (prev && pageIndex * pageSize >= prev.itemCount) return null;
    return `${cacheKey}|p=${pageIndex}`;
  };

  const fetcher = useCallback(
    (key: string) => {
      const pageIndex = Number.parseInt(key.split('|p=')[1] ?? '0', 10);
      return fetchPage({
        limit: String(pageSize),
        offset: String(pageIndex * pageSize),
      });
    },
    [fetchPage, pageSize]
  );

  const { data, size, setSize, isLoading, isValidating, mutate, error } = useSWRInfinite<
    PaginatedResponse<T>
  >(getKey, fetcher, { revalidateFirstPage: false });

  const items = useMemo(() => (data ?? []).flatMap((page) => page.items), [data]);
  const itemCount = data?.[0]?.itemCount ?? 0;
  const loadedCount = items.length;
  const hasMore = loadedCount < itemCount;
  const isLoadingMore = isValidating && (data?.length ?? 0) !== 0 && data?.length !== size;

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) setSize((s) => s + 1);
  }, [hasMore, isLoadingMore, setSize]);

  const refresh = useCallback(() => {
    mutate();
  }, [mutate]);

  return {
    items,
    itemCount,
    loadedCount,
    hasMore,
    isLoading,
    isLoadingMore,
    error,
    loadMore,
    refresh,
  };
};
